import {
  Component, OnDestroy, AfterViewInit,
  EventEmitter, Output, NgZone, ElementRef,
} from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

import { appStateProvider } from "../../insa-providers";
import { InsaService, LocalizeService } from "../../insa-services";
import { NewsDataModel } from "../../insa-models";
import { appConfig } from "../../../insaweb.config";

const nodataImg = require('../../../Images/nodata.svg');

@Component({
  selector: "insaweb-news",
  templateUrl: "news.component.html",
})
export class NewsComponent implements OnDestroy, AfterViewInit {
  nodataImg = nodataImg;
  newsData: NewsDataModel[];
  imageErrors: { [key: number]: boolean } = {};
  activeNewsForModal: NewsDataModel | null = null;
  safeBlobPdfUrl: SafeResourceUrl | null = null;
  pdfLoading = false;
  private _blobUrl: string | null = null;
  private newsDataSubscription;
  private _localeChangedSubscription;

  @Output() currentNewsTitleChange: EventEmitter<string> = new EventEmitter<string>();

  constructor(
    private _insaService: InsaService,
    private _appStateProvider: appStateProvider,
    private _router: Router,
    private _ngZone: NgZone,
    private _sanitizer: DomSanitizer,
    private _http: HttpClient,
    private _localize: LocalizeService,
    private _element: ElementRef,
  ) {
    this.newsDataSubscription = this._insaService.NewsDataEvent.subscribe(
      (news) => { this.refreshNewsData(news); },
    );

    // Re-apply translations whenever locale resources finish loading so that
    // dynamically rendered elements (e.g. "Read more" in *ngFor) are always
    // translated even if resources arrived after the carousel was rendered.
    this._localeChangedSubscription = this._localize.LocaleChangedEmmiter.subscribe(() => {
      this._localize.applyTranslations(this._element.nativeElement);
    });
  }

  onImageError(messageID: number) {
    this.imageErrors[messageID] = true;
  }

  /** True if URL points to a PDF file. */
  isPdfUrl(url: string): boolean {
    if (!url) return false;
    return url.split('?')[0].split('#')[0].toLowerCase().trim().endsWith('.pdf');
  }

  /** True if URL is an absolute external link (http/https). */
  isExternalUrl(url: string): boolean {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  }

  getSafePdfUrl(url: string): SafeResourceUrl {
    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Fetches the PDF as a blob so the browser gets raw bytes with the correct
   * Content-Type — this bypasses any IIS / SPA routing that would otherwise
   * serve the Angular app instead of the file.
   */
  private _loadPdfAsBlob(url: string): void {
    // Normalize Windows-style backslashes to forward slashes
    url = url.replace(/\\/g, '/');
    // Ensure root-relative (no leading slash → add one)
    if (!url.startsWith('/') && !url.startsWith('http')) {
      url = '/' + url;
    }
    this.pdfLoading = true;
    this.safeBlobPdfUrl = null;
    this._revokeBlobUrl();

    this._http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        this._blobUrl = URL.createObjectURL(pdfBlob);
        this.safeBlobPdfUrl = this._sanitizer.bypassSecurityTrustResourceUrl(this._blobUrl);
        this.pdfLoading = false;
      },
      error: () => {
        // Blob fetch failed — leave safeBlobPdfUrl null so the iframe
        // stays hidden (avoids loading the SPA inside it as a fallback).
        this.pdfLoading = false;
      },
    });
  }

  private _revokeBlobUrl(): void {
    if (this._blobUrl) {
      URL.revokeObjectURL(this._blobUrl);
      this._blobUrl = null;
    }
  }

  downloadPdf(url: string, title?: string): void {
    const filename = title
      ? title.replace(/[^a-zA-Z0-9_\-. ]/g, '_').trim() + '.pdf'
      : 'document.pdf';
    // Prefer the already-fetched blob so we download real PDF bytes,
    // not whatever the server might return for the path.
    const href = this._blobUrl || url;
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  openDetailsModal(news: NewsDataModel, event?: Event): void {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    this.activeNewsForModal = news;
    this.safeBlobPdfUrl = null;
    this.pdfLoading = false;

    // PRIVREMENO: ako je MessageID 57, uvijek koristi lokalni NLB PDF
    // bez obzira na DetailsUrl koji vraća backend. Obriši kad backend počne vraćati ispravnu putanju.
    if (news.MessageID === 57) {
      news = { ...news, DetailsUrl: 'insa-css\\insa-clients\\NLB\\NLB_Trader_korisnicko_uputstvo_25_05_2026.pdf' };
      this.activeNewsForModal = news;
    }

    setTimeout(() => {
      ($('#news-detail-modal') as any).modal({ show: true });
      // Apply translations to modal content rendered by *ngIf
      this._localize.applyTranslations(this._element.nativeElement);
      // Load PDF as blob so iframe shows it inline (avoids IIS/SPA routing issue)
      if (this.isPdfUrl(news.DetailsUrl)) {
        this._loadPdfAsBlob(news.DetailsUrl);
      }
    }, 0);
  }

  closeDetailsModal(): void {
    ($('#news-detail-modal') as any).modal('hide');
    setTimeout(() => {
      this.activeNewsForModal = null;
      this.safeBlobPdfUrl = null;
      this._revokeBlobUrl();
    }, 350);
  }

  private refreshNewsData(news: NewsDataModel[]) {
    if (news !== undefined && news.length > 0) {
      this.newsData = news;
      this.imageErrors = {};
      this.currentNewsTitleChange.emit(news[0].Title);
      setTimeout(this.triggerRight.bind(this), 1000);
    } else {
      this.newsData = [];
      this.currentNewsTitleChange.emit("");
    }
  }

  private triggerRight() {
    if (this.newsData && this.newsData.length > 0) {
      this.newsTextVisibility();
      // Carousel items are fully in DOM here — apply translations
      this._localize.applyTranslations(this._element.nativeElement);
      let carouselOpt: CarouselOptions = {};
      carouselOpt.interval = appConfig.setCarouselTimeout;
      $("#carousel-generic").carousel(carouselOpt);

      $("#carousel-generic").off("slid.bs.carousel.titleSync");
      $("#carousel-generic").on("slid.bs.carousel.titleSync", () => {
        this._ngZone.run(() => {
          const idx = $("#carousel-generic .item.active").index();
          if (this.newsData && this.newsData[idx]) {
            this.currentNewsTitleChange.emit(this.newsData[idx].Title);
          }
        });
      });
    }
  }

  private newsTextVisibility() {
    if (this._router.url === "/news") {
      $(".news").css("display", "block");
    } else if (this._router.url === "/home") {
      $(".news").css("display", "none");
    }
  }

  private isActive(news: any) {
    const data = this._insaService.newsData;
    return data && data.length > 0
      ? news === data[0]
      : news === (this.newsData && this.newsData[0]);
  }

  ngAfterViewInit() {
    kendo.ui.progress($(".news-loading"), this._appStateProvider.fetchingNews);
    this.refreshNewsData(this._insaService.newsData);
  }

  ngOnDestroy() {
    this._revokeBlobUrl();
    this.newsDataSubscription.unsubscribe();
    if (this._localeChangedSubscription) {
      this._localeChangedSubscription.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-news/news.component.ts